import { useTranslation as useReactTranslation } from "react-i18next";

import type { Dictionary } from "../__generated__/dictionary";

/**
 * i18next resolves plural keys (`someKey_one` / `someKey_other` / ...) at
 * runtime from the *base* key passed to `t()` plus a `count` param — the
 * suffixed form is never called directly. Derive the set of valid base keys
 * from the generated Dictionary so `t("someKey", { count })` type-checks
 * without needing the literal suffixed key.
 */
type PluralSuffix = "_zero" | "_one" | "_two" | "_few" | "_many" | "_other";
type PluralBaseKey<K> = K extends `${infer Base}${PluralSuffix}` ? Base : never;
type TranslationKey = keyof Dictionary | PluralBaseKey<keyof Dictionary>;

/**
 * Typed wrapper around `react-i18next`'s `useTranslation` hook.
 *
 * Returns a `t` function whose keys are constrained to the generated
 * `Dictionary` type (plus derived plural base keys), providing compile-time
 * safety for translation keys.
 *
 * @example
 * ```tsx
 * const { t } = useTranslation();
 * return <span>{t("common.save")}</span>;
 * ```
 */
export function useTranslation(options?: UseTranslationOptions) {
  const { t, i18n } = useReactTranslation(undefined, options);

  return {
    /** Translate a known key from the dictionary. */
    t: t as (key: TranslationKey, params?: Record<string, unknown>) => string,
    /** The underlying i18next instance (for advanced use only). */
    i18n,
  };
}

type UseTranslationOptions = {
  keyPrefix?: string;
};
